// Service d'authentification pour communiquer avec l'API Django

const API_BASE_URL = 'https://inza-backend.sajholding.org/api/v1';

class AuthService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Récupérer le token CSRF depuis les cookies
  getCSRFToken() {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  // Configuration des headers pour les requêtes
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Ajouter le token JWT depuis localStorage (pour le développement)
    const token = localStorage.getItem('token');
    if (token) {
      // Pour le développement, utiliser X-Mock-Token pour éviter le middleware JWT
      if (token === 'mock-access-token') {
        headers['X-Mock-Token'] = token;
      } else {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }

    return headers;
  }

  // Inscription
  async register(userData) {
    try {
      const response = await fetch(`${this.baseURL}/auth/users/register/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(userData),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }

      return data;
    } catch (error) {
      console.error('Erreur inscription:', error);
      throw error;
    }
  }

  // Connexion
  async login(credentials) {
    try {
      const response = await fetch(`${this.baseURL}/auth/users/login/`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(credentials),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Email ou mot de passe incorrect');
      }

      // Sauvegarder les informations utilisateur dans localStorage
      if (data.user) {
        // Sauvegarder le token JWT
        if (data.tokens && data.tokens.access) {
          localStorage.setItem('token', data.tokens.access);
        }
        
        localStorage.setItem('inza_user', JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          name: `${data.user.first_name} ${data.user.last_name}`.trim() || data.user.email.split('@')[0],
          firstName: data.user.first_name,
          lastName: data.user.last_name,
          isAuthenticated: true,
          tokens: data.tokens || {}
        }));
      }

      return data;
    } catch (error) {
      console.error('Erreur connexion:', error);
      throw error;
    }
  }

  // Récupérer le token depuis localStorage
  getToken() {
    return localStorage.getItem('token');
  }

  // Vérifier le statut d'authentification
  async checkAuthStatus() {
    try {
      // Si pas de token, retourner null directement
      if (!this.getToken()) {
        return null;
      }

      const response = await fetch(`${this.baseURL}/auth/users/status/`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Non authentifié');
      }

      return data;
    } catch (error) {
      console.error('Erreur vérification statut:', error);
      return null;
    }
  }

  // Obtenir le profil utilisateur
  async getUserProfile() {
    try {
      const response = await fetch(`${this.baseURL}/user/profile/`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur profil');
      }

      return data;
    } catch (error) {
      console.error('Erreur profil:', error);
      throw error;
    }
  }

  // Déconnexion
  async logout() {
    console.log('authService.logout called');
    try {
      // Pour le développement, on nettoie simplement le localStorage
      // sans faire d'appel API au backend
      console.log('Clearing localStorage...');
      localStorage.removeItem('inza_user');
      localStorage.removeItem('token');
      console.log('localStorage cleared');
      
      // Optionnel : tenter l'appel API si le backend est disponible
      try {
        const response = await fetch(`${this.baseURL}/auth/users/logout/`, {
          method: 'POST',
          headers: this.getHeaders(),
          credentials: 'include',
        });
        
        if (response.ok) {
          await response.json();
          console.log('API logout successful');
        } else {
          console.log('API logout failed with status:', response.status);
          // C'est normal si on est déjà déconnecté (401)
          if (response.status === 401) {
            console.log('Already logged out (401), continuing...');
          }
        }
      } catch (apiError) {
        console.log('Backend non disponible, déconnexion locale uniquement');
      }

      console.log('Returning success');
      return { success: true };
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      // Nettoyer localStorage même en cas d'erreur
      localStorage.removeItem('inza_user');
      localStorage.removeItem('token');
      throw error;
    }
  }

  // Vérifier si l'utilisateur est connecté (côté client)
  isAuthenticated() {
    const userData = localStorage.getItem('inza_user');
    if (!userData) return false;
    
    try {
      const user = JSON.parse(userData);
      return user.isAuthenticated === true;
    } catch {
      return false;
    }
  }

  // Obtenir les informations utilisateur locales
  getCurrentUser() {
    const userData = localStorage.getItem('inza_user');
    if (!userData) return null;
    
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }

  // Mettre à jour les informations utilisateur locales
  updateUser(userData) {
    localStorage.setItem('inza_user', JSON.stringify(userData));
  }
}

export default new AuthService();
